import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';

import { ApiService, AppUser } from '../../core/api.service';

@Component({
    selector: 'app-users',
    standalone: true,
    imports: [
        CommonModule, FormsModule,
        TableModule, ButtonModule, InputTextModule, PasswordModule, DialogModule, ToastModule, ConfirmDialogModule, TagModule, CheckboxModule
    ],
    providers: [MessageService, ConfirmationService],
    template: `
        <p-toast></p-toast>
        <p-confirmDialog></p-confirmDialog>

        <div class="card">
            <div class="flex justify-between items-center mb-4">
                <div class="font-semibold text-xl"><i class="pi pi-users mr-2"></i>Users</div>
                <p-button label="Add User" icon="pi pi-plus" (onClick)="openNew()"></p-button>
            </div>

            <p-table [value]="users" [loading]="loading" [paginator]="true" [rows]="10"
                styleClass="p-datatable-sm p-datatable-striped">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Username</th>
                        <th>Full Name</th>
                        <th style="width:8rem">Status</th>
                        <th style="width:11rem">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-u>
                    <tr>
                        <td>{{ u.username }}</td>
                        <td>{{ u.fullName }}</td>
                        <td>
                            <p-tag [value]="u.isActive ? 'Active' : 'Inactive'"
                                [severity]="u.isActive ? 'success' : 'secondary'"></p-tag>
                        </td>
                        <td>
                            <p-button icon="pi pi-pencil" severity="info" [text]="true" (onClick)="openEdit(u)"></p-button>
                            <p-button *ngIf="u.isActive" icon="pi pi-ban" severity="danger" [text]="true" (onClick)="confirmDelete(u)" pTooltip="Deactivate"></p-button>
                            <p-button *ngIf="!u.isActive" icon="pi pi-check-circle" severity="success" [text]="true" (onClick)="activate(u)" pTooltip="Activate"></p-button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="4" class="text-center p-6 text-muted-color">No users. Add one.</td></tr>
                </ng-template>
            </p-table>
        </div>

        <!-- Add / Edit dialog -->
        <p-dialog [header]="editId ? 'Edit User' : 'Add User'" [(visible)]="dialog" [modal]="true"
            [style]="{ width: '450px' }" [draggable]="false">
            <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Username *</label><input pInputText [(ngModel)]="item.username" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Full Name</label><input pInputText [(ngModel)]="item.fullName" /></div>
                <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium">{{ editId ? 'New Password (leave blank to keep)' : 'Password *' }}</label>
                    <p-password [(ngModel)]="item.password" [feedback]="false" [toggleMask]="true" styleClass="w-full" [inputStyle]="{ width: '100%' }"></p-password>
                </div>
                <div class="flex items-center gap-2">
                    <p-checkbox [(ngModel)]="item.isActive" [binary]="true" inputId="active"></p-checkbox>
                    <label for="active" class="text-sm font-medium">Active</label>
                </div>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="dialog = false"></p-button>
                <p-button label="Save" icon="pi pi-check" (onClick)="save()"></p-button>
            </ng-template>
        </p-dialog>
    `
})
export class Users implements OnInit {
    users: AppUser[] = [];
    loading = false;

    dialog = false;
    editId: number | null = null;
    item: AppUser = { isActive: true } as AppUser;

    constructor(
        private api: ApiService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void { this.load(); }

    load(): void {
        this.loading = true;
        this.api.getUsers().subscribe({
            next: (data) => { this.users = data; this.loading = false; this.cd.detectChanges(); },
            error: () => { this.loading = false; this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load users.' }); this.cd.detectChanges(); }
        });
    }

    openNew(): void {
        this.editId = null;
        this.item = { isActive: true } as AppUser;
        this.dialog = true;
    }

    openEdit(u: AppUser): void {
        this.editId = u.id;
        this.item = { ...u, password: '' };  // password blank (keep old unless typed)
        this.dialog = true;
    }

    save(): void {
        if (!this.item.username || !this.item.username.trim()) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Username is required.' });
            return;
        }
        if (!this.editId && (!this.item.password || !this.item.password.trim())) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Password is required.' });
            return;
        }

        const obs = this.editId
            ? this.api.updateUser(this.editId, this.item)
            : this.api.createUser(this.item);

        obs.subscribe({
            next: () => {
                this.dialog = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: this.editId ? 'User updated.' : 'User created.' });
                this.load();
                this.cd.detectChanges();
            },
            error: (err) => { this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Save failed.' }); this.cd.detectChanges(); }
        });
    }

    confirmDelete(u: AppUser): void {
        this.confirmationService.confirm({
            message: `Deactivate user "${u.username}"?`,
            header: 'Confirm Deactivate',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, deactivate',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.deleteUser(u.id).subscribe({
                    next: (res: any) => { this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Deactivated.' }); this.load(); this.cd.detectChanges(); },
                    error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed.' }); this.cd.detectChanges(); }
                });
            }
        });
    }

    activate(u: AppUser): void {
        this.api.activateUser(u.id).subscribe({
            next: (res: any) => { this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Activated.' }); this.load(); this.cd.detectChanges(); },
            error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Activate failed.' }); this.cd.detectChanges(); }
        });
    }
}
